import { alertController, toastController } from "@ionic/vue";

// Separator when several error details are shown in one alert
export const ERROR_SEPARATOR = "\n\n##########\n\n";

// Full, copyable dump of an axios error (request, response, device) for debugging.
// `context` is any extra info about what was being done (action, ids, file info...).
export function buildErrorDetails(err, context = {}) {
  const stringify = (value) => {
    if (value === undefined) return "undefined";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return String(value);
    }
  };
  const config = (err && err.config) || {};
  const response = (err && err.response) || null;
  // never put the auth token in a text the user will copy/share
  const redactHeaders = (headers) => {
    if (!headers) return headers;
    const copy = JSON.parse(JSON.stringify(headers));
    const redact = (obj) => {
      Object.keys(obj || {}).forEach((key) => {
        if (obj[key] && typeof obj[key] === "object") redact(obj[key]);
        else if (key.toLowerCase() === "authorization") obj[key] = "[OMESSO]";
      });
    };
    redact(copy);
    return copy;
  };

  const sections = [
    ["DATA", new Date().toString()],
    ["CONTESTO", stringify(context)],
    ["MESSAGGIO", (err && err.message) || String(err)],
    ["CODICE", (err && err.code) || "-"],
    [
      "RICHIESTA",
      stringify({
        method: config.method,
        baseURL: config.baseURL,
        url: config.url,
        timeout: config.timeout,
        headers: redactHeaders(config.headers),
      }),
    ],
    [
      "RISPOSTA",
      response
        ? stringify({
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          })
        : "Nessuna risposta dal server (rete, CORS o timeout)",
    ],
    ["CORPO RISPOSTA", response ? stringify(response.data) : "-"],
    ["USER AGENT", navigator.userAgent],
    ["URL PAGINA", window.location.href],
    ["STACK", (err && err.stack) || "-"],
  ];

  return sections.map(([title, body]) => `=== ${title} ===\n${body}`).join("\n\n");
}

export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    console.warn("clipboard API failed, using fallback", e);
  }
  // Fallback (older iOS / non-https): hidden textarea + execCommand
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length); // required on iOS
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (e) {
    return false;
  }
}

async function presentToast(message, color) {
  const toast = await toastController.create({
    message,
    duration: color === "success" ? 1500 : 3000,
    position: "bottom",
    color,
  });
  await toast.present();
}

// Alert with the full error in a readonly textarea + "Copia" button.
// Styles: `.error-details-alert` in theme/basic.scss
export async function showErrorDetails(header, details) {
  const alert = await alertController.create({
    header,
    subHeader: "Premi \"Copia\" per copiare tutto l'errore",
    cssClass: "error-details-alert",
    inputs: [
      {
        name: "details",
        type: "textarea",
        value: details,
        attributes: { readonly: true, rows: 12 },
      },
    ],
    buttons: [
      { text: "Chiudi", role: "cancel" },
      {
        text: "Copia",
        handler: () => {
          copyToClipboard(details).then((ok) =>
            presentToast(
              ok ? "Errore copiato negli appunti" : "Impossibile copiare, seleziona il testo a mano",
              ok ? "success" : "warning"
            )
          );
          return false; // keep the alert open
        },
      },
    ],
  });
  await alert.present();
}
